/**
 * InterestedLeadModal
 * ---------------------------------------------------------------
 * This modal is used when updating a lead that becomes "Interested".
 * It allows the user to update:
 *   - Name details
 *   - Property preferences (type, subtype, stage)
 *   - Budget, project, location
 *   - Lead source
 *   - Add notes
 *   - Set next follow-up date
 *   - Create an auto task for follow-up
 *
 * It also:
 *   - Fetches dynamic dropdown data (projects, locations, budgets, etc.)
 *   - Fetches user location (latitude / longitude)
 *   - Saves updated contact + task + note to Firebase
 */

import { FunctionComponent, useEffect, useState, useRef } from "react";
import { addNoteFirebase, contactResources, createTaskFirebase, fetchConstants, fetchNewPropertySubType, fetchpropertySubType, fetchpropertyType, validateInterested } from "../../../Services/contacts";
import { connect, useDispatch } from "react-redux";
import TextInput from "../../TextInput/InputText";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import PhoneNumberWrapper from "../../TextInput/PhoneNumberWrapper";
import { getLocation } from "../../../Values/utils";
import DropDown from "../../../Screens/LeadDistributorLogic/Dropdown"
import InputTextDate from "../../TextInput/InputTextDate";

export interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
  format: string;
}

type props = {
  contactDetail: any;
  close: () => void;
  open: boolean;
  notes: any[];
  tasksData: any[];
  user: any;
  theme: any;
  conatctHeader: any;
};

const InterestedLeadModal: FunctionComponent<props> = ({
  contactDetail,
  close,
  open,
  notes,
  tasksData,
  user,
  theme,
  conatctHeader,
}) => {
  /** --------------------- Hooks & Responsive --------------------- */
  const dispatcher = useDispatch();
  const history = useHistory();
  const location: any = useLocation();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** --------------------- Refs --------------------- */
  const firstNameRef: any = useRef();
  const lastNameRef: any = useRef();
  const noteRef: any = useRef();

  /** --------------------- Component State --------------------- */
  const [load, setLoad] = useState(false);

  // Populate dropdown default values from existing contact data
  const [projectSelected, setProjectSelected] = useState<any>({ label: contactDetail.project ? contactDetail.project : "Select", value: contactDetail.project ? contactDetail.project : "Select" });
  const [nextFollowDate, setNextFollowDate] = useState<any>('');

  // Alternate number fields
  const [alternatePhoneCountryCode, setAlternatePhoneCountryCode] = useState('in');
  const [alternatePhoneNumber, setAlternatePhoneNumber] = useState('');
  const [finalAlternatePhoneNumber, setFinalAlternatePhoneNumber] = useState('');

  // Other dropdown selections
  const [locationSelected, setlocationSelected] = useState<any>({ label: contactDetail.location ? contactDetail.location : "Select", value: contactDetail.location ? contactDetail.location : "Select" });
  const [budgetSelected, setBudgetSelected] = useState<any>({ label: contactDetail.budget ? contactDetail.budget : "Select", value: contactDetail.budget ? contactDetail.budget : "Select" });
  const [propertyTypeSelected, setPropertyTypeSelected] = useState<any>({ label: contactDetail.property_type ? contactDetail.property_type : "Select", value: contactDetail.property_type ? contactDetail.property_type : "Select" });
  const [propertyStageSelected, setPropertyStageSelected] = useState<any>({ label: contactDetail.property_stage ? contactDetail.property_stage : "Select", value: contactDetail.property_stage ? contactDetail.property_stage : "Select" });
  const [nextFollowSelected, setNextFollowSelected] = useState<any>({ label: contactDetail.next_follow_up_type ? contactDetail.next_follow_up_type : "Select", value: contactDetail.next_follow_up_type ? contactDetail.next_follow_up_type : "Select" });
  const [propertySubTypeSelected, setPropertySubTypeSelected] = useState<any>({ label: contactDetail.property_sub_type ? contactDetail.property_sub_type : "Select", value: contactDetail.property_sub_type ? contactDetail.property_sub_type : "Select" });
  const [leadSourceSelected, setLeadSourceSelected] = useState<any>({ label: contactDetail.lead_source ? contactDetail.lead_source : "Select", value: contactDetail.lead_source ? contactDetail.lead_source : "Select" });

  /** --------------------- Dropdown Lists --------------------- */
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [locationsList, setLocationsList] = useState<any[]>([]);
  const [budgetsList, setBudgetsList] = useState<any[]>([]);
  const [oldPropertyTypeList, setOldPropertyTypeList] = useState<any[]>([]);
  const [allPropertySubTypeList, setAllPropertySubTypeList] = useState<any[]>([]);
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [nextFollowUpList, setnextFollowUpList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);
  const [comTypes, setComTypes] = useState<any[]>([]);
  const [resTypes, setResTypes] = useState<any[]>([]);
  const [propertySubType, setPropertySubType] = useState<any[]>([]);
  const [leadSourceList, setLeadSourceList] = useState<any[]>([]);
  const [latitude, setLatitude] = useState<any>(null);
  const [longitude, setLongitude] = useState<any>(null);

  /** ------------------------------------------------------------------
 * Fetch dropdown resources:
 *  - Projects, locations, budgets
 *  - Property types and subtypes
 *  - Constants (stages, next follow-up, not interested reason)
 * ------------------------------------------------------------------ */
  useEffect(() => {
    const unsub = contactResources(
      contactDetail.organization_id,
      (data) => setLocationsList(data),
      (data) => setBudgetsList(data),
      (data) => setProjectsList(data)
    );
    const unNewPropertyType = fetchpropertyType(
      user.organization_id,
      (data) => setPropertyTypeList(data)
    );
    const unNewPropertySubType = fetchNewPropertySubType(
      user.organization_id,
      (data) => setAllPropertySubTypeList(data)
    );
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setOldPropertyTypeList(data),
      (data) => setnextFollowUpList(data)
    );
    const unSubProperty = fetchpropertySubType(
      contactDetail.organization_id,
      (data) => setComTypes(data),
      (data) => setResTypes(data),
      (data) => setLeadSourceList(data)
    );
    return () => {
      unsub();
      unsubConst();
      unSubProperty();
      unNewPropertyType();
      unNewPropertySubType();
    };
  }, []);

  /** ------------------------------------------------------------------
  * Update Subtype list when Property Type changes
  * ------------------------------------------------------------------ */
  useEffect(() => {
    const filteredArray = allPropertySubTypeList.filter(item => item.property_type === propertyTypeSelected.value);
    const propertySubTypes = filteredArray.map(item => item.property_sub_type);
    if (propertySubTypes.length !== 0) {
      setPropertySubType(propertySubTypes);
    } else {
      setPropertySubType([]);
    }
  }, [propertyTypeSelected]);

  /** ------------------------------------------------------------------
   * Fetch user's live location using browser permissions
   * Used to store correct metadata for follow-up task created
   * ------------------------------------------------------------------ */

  const fetchUserLocation = async () => {
    const position = await getLocation();

    if (!position) {
      console.warn("Location not granted yet");
      return false;
    }

    setLatitude(position.latitude);
    setLongitude(position.longitude);
    return true;
  };

  useEffect(() => {
    if (open) {
      fetchUserLocation();
    }
  }, [open]);
  /** ------------------------------------------------------------------
   * Alternate Phone Input Handler
   * Ensures final saved value removes country code duplication
   * ------------------------------------------------------------------ */
  const handleAlternatePhoneChange = (value: string, country: CountryData) => {
    if (value.startsWith(country?.dialCode)) {
      const phoneNumberWithoutCode = value.substring(country?.dialCode.length);
      setFinalAlternatePhoneNumber(phoneNumberWithoutCode);
    } else {
      setFinalAlternatePhoneNumber(value);
    }
    setAlternatePhoneCountryCode(country?.dialCode);
    setAlternatePhoneNumber(value)
  };

  /** ------------------------------------------------------------------
 * Submit Handler
 * Validates all fields & updates Firebase:
 *   - Creates a follow-up task
 *   - Adds a note
 *   - Updates contact details
 *   - Saves modified values to Router state
 * ------------------------------------------------------------------ */
  const onSubmit = async () => {

    const data = validateInterested(
      firstNameRef.current.value,
      lastNameRef.current.value || "",
      propertyTypeSelected.value,
      propertySubTypeSelected.value === "Select" ? propertySubTypeSelected.value : "",
      propertyStageSelected.value,
      locationSelected.value,
      projectSelected.value,
      budgetSelected.value,
      nextFollowSelected.value,
      nextFollowDate,
      leadSourceSelected.value === "Select" ? "" : leadSourceSelected.value,
      finalAlternatePhoneNumber,
      propertySubTypeSelected.value === "Select" ? "" : propertySubTypeSelected.value,
      dispatcher
    );

    const taskData = {
      call_back_reason: "",
      due_date: moment(nextFollowDate).toDate(),
      type: nextFollowSelected.value,
      status: "Pending",
      customer_name: `${firstNameRef.current?.value || ""} ${lastNameRef.current?.value || ""}`.trim(),
    };

    if (data != undefined) {

      // -------------------------------
      // STEP 1: Save note (if exists)
      // -------------------------------
      if (noteRef.current.value.length !== 0) {
        addNoteFirebase(
          contactDetail.contactId,
          notes,
          noteRef.current.value,
          () => { },
          dispatcher,
          () => { },
          user.user_email
        );
      }

      // -------------------------------
      // STEP 2: Ensure location exists
      // -------------------------------
      const position = await getLocation();

      if (!position) {
        alert(
          "Location access is required to create follow-up task.\n\n" +
          "Please enable location from browser settings:\n" +
          "🔒 Address bar → Location → Allow"
        );
        return; // ❌ stop submit safely
      }

      const lat = position.latitude;
      const lng = position.longitude;

      // Optional: keep for UI/debug
      setLatitude(lat);
      setLongitude(lng);
      // -------------------------------
      // STEP 3: Create task (GUARANTEED)
      // -------------------------------
      createTaskFirebase(
        contactDetail.contactId,
        tasksData,
        taskData,
        data,
        (value: boolean) => setLoad(value),
        dispatcher,
        user,
        close,
        history,
        undefined,
        lat,
        lng
      );

      // -------------------------------
      // STEP 4: Update router state
      // -------------------------------
      for (let p in data) {
        let key = p;
        //@ts-ignore
        location.state.detail[`${key}`] = data[p];
      }
      location.state.detail.modified_at = data.modified_at.toDate();
      location.state.detail.stage_change_at = data.stage_change_at.toDate();
    }
  };

  /** --------------------- Modal Style --------------------- */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      width: 800, // Set fixed width
      maxWidth: 900, // Increase maxWidth
      // minWidth: 600, // Increase minWidth
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /** --------------------- JSX Rendering --------------------- */
  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 900,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Form Starts */}
          <form style={{ width: "100%" }}>
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                Interested Details
              </h3>
            </Box>
            {/* FORM FIELDS */}
            <Grid container spacing={2} sx={{ width: "100%" }}>
              {/* Change sm={4} to sm={4} for 4 columns per row on desktop */}
              <Grid item xs={12} sm={4}>
                <TextInput
                  title="First Name"
                  ref={firstNameRef}
                  value={contactDetail.customer_name
                    .split(" ")[0]
                    .trim()}
                  required={true}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextInput
                  title="Last Name"
                  ref={lastNameRef}
                  // required={true}
                  value={contactDetail.customer_name
                    .slice(
                      contactDetail.customer_name.split(
                        " "
                      )[0].length
                    )
                    .trim()}
                />
              </Grid>
              {/* Location */}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "location").value}
                  option={locationsList}
                  selectedValue={locationSelected.value === "Select" ? "" : locationSelected}
                  setSelectedValue={(value) => {
                    setlocationSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Project */}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "project").value}
                  option={projectsList}
                  selectedValue={projectSelected.value === "Select" ? "" : projectSelected}
                  setSelectedValue={(value) => {
                    setProjectSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Next Follow Up Type */}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "next_follow_up_type").value}
                  option={nextFollowUpList}
                  selectedValue={nextFollowSelected.value === "Select" ? "" : nextFollowSelected}
                  setSelectedValue={(value) => {
                    setNextFollowSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Budget */}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "budget").value}
                  option={budgetsList}
                  selectedValue={budgetSelected.value === "Select" ? "" : budgetSelected}
                  setSelectedValue={(value) => {
                    setBudgetSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Property Type */}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "property_type").value}
                  option={propertyTypeList}
                  selectedValue={propertyTypeSelected.value === "Select" ? "" : propertyTypeSelected}
                  setSelectedValue={(value) => {
                    setPropertyTypeSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Property Stage */}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "property_stage").value}
                  option={propertyStageList}
                  selectedValue={propertyStageSelected.value === "Select" ? "" : propertyStageSelected}
                  setSelectedValue={(value) => {
                    setPropertyStageSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Next Follow Up Date */}
              <Grid item xs={12} sm={4}>
                <InputTextDate
                  type="datetime-local"
                  title="Next Follow Up Date"
                  value={nextFollowDate}
                  onChange={(e: any) => setNextFollowDate(e.target.value)}
                  required={true}
                />
              </Grid>
              {/* Note */}
              <Grid item xs={12} sm={4}>
                <TextInput
                  title="Note"
                  style={{ backgroundColor: "#fff" }}
                  ref={noteRef}
                />
              </Grid>
              {/* Property sub type*/}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "property_sub_type").value}
                  option={propertySubType}
                  selectedValue={propertySubTypeSelected.value === "Select" ? "" : propertySubTypeSelected}
                  setSelectedValue={(value) => {
                    setPropertySubTypeSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Lead Source*/}
              <Grid item xs={12} sm={4}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "lead_source").value}
                  option={leadSourceList}
                  selectedValue={leadSourceSelected.value === "Select" ? "" : leadSourceSelected}
                  setSelectedValue={(value) => {
                    setLeadSourceSelected(value);
                  }}
                  required={true}
                />
              </Grid>
              {/* Alternate Number */}
              <Grid item xs={12} sm={4}>
                <PhoneNumberWrapper
                  title={conatctHeader.find((obj: any) => obj.label === "alternate_no").value}
                  country={alternatePhoneCountryCode || 'in'}
                  value={alternatePhoneNumber || contactDetail.alternate_no}
                  handlePhoneChange={handleAlternatePhoneChange}
                />
              </Grid>
            </Grid>
            {/* Submit Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                <Box style={{ marginRight: 8 }}>
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>
                <Box>
                  <StyledSubmitButton
                    onClick={onSubmit}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} /> Submit...
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal >
    </>
  );
};

/** Redux Mappings */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode,
    conatctHeader: state.contactHeaderList.data,
  };
};
export default connect(mapStateToProps)(
  InterestedLeadModal
);
